/**
 * 🏢 RESPONSIBLE FINALIZATION FORM
 * Formulário para finalizar cadastro de responsável incompleto
 * Usa SCSS modular e gradients do theme/
 *
 * ✅ ATUALIZADO: Agora tem a mesma experiência do ResponsibleRegistrationForm
 * com tela de seleção PF/PJ no início e campos obrigatórios completos
 */

import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/api';
import styles from '../../theme/responsibles-form.module.scss';

const ResponsibleFinalizationForm = ({ responsible, onSuccess, onCancel }) => {

  // 🎯 Estado para tipo de pessoa (null = não selecionado, 'PF' ou 'PJ')
  const [personType, setPersonType] = useState(null);

  // Estados do formulário
  const [formData, setFormData] = useState({
    // Dados do sócio administrador (já preenchidos do cadastro inicial)
    admin_name: responsible?.admin_name || '',
    admin_cpf: responsible?.admin_cpf || '',
    admin_birth_date: '',
    admin_mother_name: '',
    monthly_income: '',
    professional_occupation: '',

    // Endereço
    address_cep: '',
    address_street: '',
    address_number: '',
    address_complement: '',
    address_neighborhood: '',
    address_city: '',
    address_state: '',
    address_reference: '',

    // Empresa (se tiver CNPJ)
    responsible_cpf: '', // CNPJ
    state_registration: '',
    company_name: '',
    trading_name: '',
    responsible_phone: '',
    founding_date: '',
    site_url: '',
    annual_revenue: '',

    // Flag CNPJ
    has_cnpj: false
  });

  const [errors, setErrors] = useState({});
  const [cepLoading, setCepLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [documents, setDocuments] = useState({
    cnh: null,
    cnpj: null,
    inscricao_estadual: null
  });

  // ✅ Estados para documentos existentes
  const [existingDocuments, setExistingDocuments] = useState({
    cnh: null,
    cnpj: null,
    inscricao_estadual: null
  });
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  // 🎯 Função para selecionar tipo de pessoa
  const handlePersonTypeSelect = (type) => {
    setPersonType(type);
    setFormData(prev => ({
      ...prev,
      has_cnpj: type === 'PJ',
      // Limpar campos de empresa se mudar para PF
      ...(type === 'PF' ? {
        responsible_cpf: '',
        state_registration: '',
        company_name: '',
        trading_name: '',
        responsible_phone: '',
        founding_date: '',
        site_url: '',
        annual_revenue: ''
      } : {})
    }));
    setErrors({});
  };

  // Buscar CEP automaticamente
  const handleCepSearch = async (cep) => {
    if (!cep || cep.length < 8) return;

    setCepLoading(true);

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();

      if (data.erro) {
        toast.warning('CEP não encontrado');
        return;
      }

      setFormData(prev => ({
        ...prev,
        address_cep: cep,
        address_street: data.logradouro || '',
        address_neighborhood: data.bairro || '',
        address_city: data.localidade || '',
        address_state: data.uf || ''
      }));

      toast.success('Endereço preenchido automaticamente!');

    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP');
    } finally {
      setCepLoading(false);
    }
  };

  // Manipular upload de documentos
  const handleDocumentUpload = (docType, file) => {
    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato inválido. Use PDF, JPG ou PNG');
      return;
    }

    // Validar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Tamanho máximo: 5MB');
      return;
    }

    setDocuments(prev => ({
      ...prev,
      [docType]: file
    }));

    toast.success(`Documento adicionado!`);
  };

  // Remover documento
  const handleRemoveDocument = (docType) => {
    setDocuments(prev => ({
      ...prev,
      [docType]: null
    }));

    toast.info(`Documento removido`);
  };

  // ✅ Carregar documentos existentes
  useEffect(() => {
    const loadExistingDocuments = async () => {
      if (!responsible?.id) return;

      setLoadingDocuments(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/responsibles/${responsible.id}/documents`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (response.ok && data.success && data.data) {
          const docMap = {
            cnh: null,
            cnpj: null,
            inscricao_estadual: null
          };

          data.data.forEach(doc => {
            if (doc.document_type === 'CNH') {
              docMap.cnh = doc;
            } else if (doc.document_type === 'CNPJ') {
              docMap.cnpj = doc;
            } else if (doc.document_type === 'INSCRICAO_ESTADUAL') {
              docMap.inscricao_estadual = doc;
            }
          });

          setExistingDocuments(docMap);
        }
      } catch (error) {
        console.error('Erro ao carregar documentos:', error);
      } finally {
        setLoadingDocuments(false);
      }
    };

    loadExistingDocuments();
  }, [responsible?.id]);

  // 🔄 Carregar dados do responsável quando disponível
  useEffect(() => {
    if (responsible) {
      // Converter data de nascimento para formato YYYY-MM-DD (se existir)
      let birthDate = '';
      if (responsible.admin_birth_date) {
        const date = new Date(responsible.admin_birth_date);
        birthDate = date.toISOString().split('T')[0];
      }

      // Converter founding_date se existir
      let foundingDate = '';
      if (responsible.founding_date) {
        const date = new Date(responsible.founding_date);
        foundingDate = date.toISOString().split('T')[0];
      }

      // Converter valores monetários (de centavos para R$)
      let monthlyIncome = '';
      if (responsible.monthly_income) {
        const value = responsible.monthly_income / 100;
        monthlyIncome = value.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2
        });
      }

      let annualRevenue = '';
      if (responsible.annual_revenue) {
        const value = responsible.annual_revenue / 100;
        annualRevenue = value.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2
        });
      }

      // Determinar tipo de pessoa baseado nos dados existentes
      const hasCnpj = !!responsible.responsible_cpf && responsible.responsible_cpf !== responsible.admin_cpf;
      if (hasCnpj) {
        setPersonType('PJ');
      }

      setFormData({
        admin_name: responsible.admin_name || '',
        admin_cpf: responsible.admin_cpf || '',
        admin_birth_date: birthDate,
        admin_mother_name: responsible.admin_mother_name || '',
        monthly_income: monthlyIncome,
        professional_occupation: responsible.professional_occupation || '',

        address_cep: responsible.address_cep || '',
        address_street: responsible.address_street || '',
        address_number: responsible.address_number || '',
        address_complement: responsible.address_complement || '',
        address_neighborhood: responsible.address_neighborhood || '',
        address_city: responsible.address_city || '',
        address_state: responsible.address_state || '',
        address_reference: responsible.address_reference || '',

        responsible_cpf: responsible.responsible_cpf || '',
        state_registration: responsible.state_registration || '',
        company_name: responsible.company_name || '',
        trading_name: responsible.trading_name || '',
        responsible_phone: responsible.responsible_phone || '',
        founding_date: foundingDate,
        site_url: responsible.site_url || '',
        annual_revenue: annualRevenue,

        has_cnpj: hasCnpj
      });
    }
  }, [responsible]);

  // Manipular mudanças nos campos
  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    // Máscaras
    if (name === 'admin_cpf') {
      finalValue = value.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (name === 'responsible_cpf') {
      finalValue = value.replace(/\D/g, '').replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    } else if (name === 'address_cep') {
      finalValue = value.replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2');
      const cepNumbers = value.replace(/\D/g, '');
      if (cepNumbers.length === 8) {
        handleCepSearch(cepNumbers);
      }
    } else if (name === 'responsible_phone') {
      finalValue = value.replace(/\D/g, '').replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (name === 'monthly_income' || name === 'annual_revenue') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue === '') {
        finalValue = '';
      } else {
        const number = parseInt(numericValue) / 100;
        finalValue = number.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2
        });
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Validar formulário
  const validateForm = () => {
    const newErrors = {};

    // Dados do administrador/responsável
    if (!formData.admin_cpf) newErrors.admin_cpf = 'CPF é obrigatório';
    if (!formData.admin_birth_date) newErrors.admin_birth_date = 'Data de nascimento é obrigatória';
    if (!formData.admin_mother_name) newErrors.admin_mother_name = 'Nome da mãe é obrigatório';
    if (!formData.monthly_income) newErrors.monthly_income = 'Renda mensal é obrigatória';
    if (!formData.professional_occupation) newErrors.professional_occupation = 'Ocupação profissional é obrigatória';

    // Validar idade (maior de 18 anos)
    if (formData.admin_birth_date) {
      const birthDate = new Date(formData.admin_birth_date);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (age < 18 || (age === 18 && monthDiff < 0)) {
        newErrors.admin_birth_date = 'Administrador deve ser maior de 18 anos';
      }
    }

    // Documento obrigatório
    if (!documents.cnh && !existingDocuments.cnh) {
      newErrors.cnh = 'Documento pessoal (CNH ou RG) é obrigatório';
    }

    // Endereço
    if (!formData.address_cep) newErrors.address_cep = 'CEP é obrigatório';
    if (!formData.address_street) newErrors.address_street = 'Rua é obrigatória';
    if (!formData.address_number) newErrors.address_number = 'Número é obrigatório';
    if (!formData.address_neighborhood) newErrors.address_neighborhood = 'Bairro é obrigatório';
    if (!formData.address_city) newErrors.address_city = 'Cidade é obrigatória';
    if (!formData.address_state) newErrors.address_state = 'Estado é obrigatório';
    if (!formData.address_reference) newErrors.address_reference = 'Ponto de referência é obrigatório';

    // Se tem CNPJ, validar campos da empresa
    if (personType === 'PJ') {
      if (!formData.responsible_cpf) newErrors.responsible_cpf = 'CNPJ é obrigatório';
      if (!formData.company_name) newErrors.company_name = 'Razão Social é obrigatória';
      if (!formData.trading_name) newErrors.trading_name = 'Nome Fantasia é obrigatório';
      if (!formData.founding_date) newErrors.founding_date = 'Data de fundação é obrigatória';
      if (!formData.site_url) newErrors.site_url = 'Site da empresa é obrigatório';
      if (!formData.responsible_phone) newErrors.responsible_phone = 'Telefone da empresa é obrigatório';
      if (!formData.annual_revenue) newErrors.annual_revenue = 'Faturamento anual é obrigatório';

      if (formData.site_url) {
        try {
          const url = new URL(formData.site_url);
          if (!url.protocol.startsWith('http')) {
            newErrors.site_url = 'URL deve começar com http:// ou https://';
          }
        } catch (e) {
          newErrors.site_url = 'URL inválida. Exemplo: https://www.empresa.com.br';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submeter formulário
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');

      // Limpar máscaras e converter valores
      const cleanedData = {
        ...formData,
        admin_cpf: formData.admin_cpf.replace(/\D/g, ''),
        responsible_cpf: formData.responsible_cpf.replace(/\D/g, ''),
        address_cep: formData.address_cep.replace(/\D/g, ''),
        responsible_phone: formData.responsible_phone.replace(/\D/g, ''),
        monthly_income: formData.monthly_income ?
          Math.round(parseFloat(formData.monthly_income.replace(/[^\d,]/g, '').replace(',', '.')) * 100) : null,
        annual_revenue: formData.annual_revenue ?
          Math.round(parseFloat(formData.annual_revenue.replace(/[^\d,]/g, '').replace(',', '.')) * 100) : null
      };

      const response = await fetch(`${API_URL}/api/responsibles/${responsible.id}/complete`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(cleanedData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Enviar documentos (se houver)
        const documentsToUpload = [];
        if (documents.cnh) documentsToUpload.push({ type: 'CNH', file: documents.cnh });
        if (documents.cnpj) documentsToUpload.push({ type: 'CNPJ', file: documents.cnpj });
        if (documents.inscricao_estadual) documentsToUpload.push({ type: 'INSCRICAO_ESTADUAL', file: documents.inscricao_estadual });

        if (documentsToUpload.length > 0) {
          for (const doc of documentsToUpload) {
            try {
              const existingDoc = existingDocuments[doc.type.toLowerCase()];
              if (existingDoc && existingDoc.id) {
                try {
                  await fetch(`${API_URL}/api/responsibles/${responsible.id}/documents/${existingDoc.id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                } catch (deleteError) {
                  console.error('Erro ao deletar documento antigo:', deleteError);
                }
              }

              const uploadFormData = new FormData();
              uploadFormData.append('document', doc.file);
              uploadFormData.append('document_type', doc.type);

              const uploadResponse = await fetch(`${API_URL}/api/upload/document?responsible_id=${responsible.id}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: uploadFormData
              });

              const uploadData = await uploadResponse.json();
              if (!uploadResponse.ok) {
                console.warn(`Erro ao enviar ${doc.type}:`, uploadData.message);
              }
            } catch (docError) {
              console.error(`Erro ao enviar documento ${doc.type}:`, docError);
            }
          }

          toast.success('Cadastro e documentos enviados com sucesso!');
        } else {
          toast.success('Cadastro finalizado com sucesso!');
        }

        if (onSuccess) {
          onSuccess(data.data);
        }
      } else {
        throw new Error(data.message || 'Erro ao finalizar cadastro');
      }

    } catch (error) {
      console.error('Erro ao finalizar cadastro:', error);
      toast.error(error.message || 'Erro ao finalizar cadastro');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles['responsible-form-container']}>
      {/* Header do formulário */}
      <div className={styles['form-header']}>
        <span style={{ fontSize: '40px', display: 'block', fontWeight: '600', marginBottom: '8px' }}>
          <Icon icon="mdi:account-check" />
          Finalizar Cadastro de Responsável
        </span>
        <p>Complete os dados para liberar todas as funcionalidades da plataforma</p>
      </div>

      {/* Alert de cadastro incompleto */}
      <div className={styles['incomplete-alert']} style={{borderTopLeftRadius: 0, borderTopRightRadius: 0, borderLeft: 0}}>
        <Icon icon="mdi:alert-circle" />
        <div>
          <strong>Cadastro Incompleto</strong>
          <span>Complete as informações abaixo para validar sua conta</span>
        </div>
      </div>

      {/* 🎯 TELA DE SELEÇÃO PF/PJ */}
      {!personType ? (
        <div className={styles['form-card']} style={{ textAlign: 'center', padding: '2rem' }}>
          <div className={styles['section-header']} style={{ justifyContent: 'center', marginBottom: '1.5rem' }}>
            <Icon icon="mdi:account-question" />
            <span style={{ fontSize: '24px', display: 'block', fontWeight: '600' }}>
              Qual o tipo de cadastro?
            </span>
          </div>

          <p style={{ color: '#666', marginBottom: '2rem' }}>
            Selecione o tipo de pessoa para continuar o cadastro
          </p>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            {/* Opção Pessoa Física */}
            <div
              onClick={() => handlePersonTypeSelect('PF')}
              style={{
                border: '2px solid #e0e0e0',
                borderRadius: '12px',
                padding: '1.5rem 2rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minWidth: '200px',
                background: '#fff'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#6366f1';
                e.currentTarget.style.background = '#f5f5ff';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#e0e0e0';
                e.currentTarget.style.background = '#fff';
              }}
            >
              <Icon icon="mdi:account" style={{ fontSize: '48px', color: '#6366f1', marginBottom: '0.5rem' }} />
              <div style={{ fontWeight: '600', fontSize: '18px', marginBottom: '0.5rem' }}>Pessoa Física</div>
              <div style={{ fontSize: '13px', color: '#666' }}>CPF - Para afiliados</div>
            </div>

            {/* Opção Pessoa Jurídica */}
            <div
              onClick={() => handlePersonTypeSelect('PJ')}
              style={{
                border: '2px solid #e0e0e0',
                borderRadius: '12px',
                padding: '1.5rem 2rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minWidth: '200px',
                background: '#fff'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#10b981';
                e.currentTarget.style.background = '#f0fdf4';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#e0e0e0';
                e.currentTarget.style.background = '#fff';
              }}
            >
              <Icon icon="mdi:domain" style={{ fontSize: '48px', color: '#10b981', marginBottom: '0.5rem' }} />
              <div style={{ fontWeight: '600', fontSize: '18px', marginBottom: '0.5rem' }}>Pessoa Jurídica</div>
              <div style={{ fontSize: '13px', color: '#666' }}>CNPJ - Para produtores</div>
            </div>
          </div>

          {/* Botão Cancelar */}
          <div style={{ marginTop: '2rem' }}>
            <button
              type="button"
              className={styles['btn-cancel']}
              onClick={onCancel}
              style={{ padding: '0.5rem 1.5rem' }}
            >
              <Icon icon="mdi:close" />
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        /* 🎯 FORMULÁRIO (aparece após seleção) */
        <form onSubmit={handleSubmit} className={styles['form-card']}>
          {/* Indicador de tipo selecionado + botão voltar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1.5rem',
            padding: '0.75rem 1rem',
            background: personType === 'PJ' ? '#f0fdf4' : '#f5f5ff',
            borderRadius: '8px',
            border: `1px solid ${personType === 'PJ' ? '#10b981' : '#6366f1'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Icon
                icon={personType === 'PJ' ? 'mdi:domain' : 'mdi:account'}
                style={{ fontSize: '20px', color: personType === 'PJ' ? '#10b981' : '#6366f1' }}
              />
              <span style={{ fontWeight: '500' }}>
                {personType === 'PJ' ? 'Pessoa Jurídica (CNPJ)' : 'Pessoa Física (CPF)'}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setPersonType(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#666',
                cursor: 'pointer',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}
            >
              <Icon icon="mdi:arrow-left" />
              Alterar tipo
            </button>
          </div>

          {/* Seção: Sócio Administrador */}
          <div className={styles['section-header']}>
            <Icon icon="mdi:account-star" />
            <span style={{ fontSize: '32px', display: 'block', fontWeight: '600' }}>
              {personType === 'PJ' ? 'Dados do Sócio Administrador' : 'Dados Pessoais'}
            </span>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className={styles['form-group']}>
                <label>
                  Nome Completo <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="admin_name"
                  value={formData.admin_name}
                  onChange={handleChange}
                  disabled
                  placeholder="Nome do sócio administrador"
                />
                <span className={styles['form-text']}>Preenchido automaticamente do cadastro</span>
              </div>
            </div>

            <div className="col-md-3">
              <div className={styles['form-group']}>
                <label>
                  CPF <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="admin_cpf"
                  value={formData.admin_cpf}
                  onChange={handleChange}
                  className={errors.admin_cpf ? styles['is-invalid'] : ''}
                  placeholder="000.000.000-00"
                  maxLength="14"
                />
                {errors.admin_cpf && (
                  <div className={styles['invalid-feedback']}>
                    <Icon icon="mdi:alert-circle" />
                    {errors.admin_cpf}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-3">
              <div className={styles['form-group']}>
                <label>
                  Data de Nascimento <span className={styles.required}>*</span>
                </label>
                <input
                  type="date"
                  name="admin_birth_date"
                  value={formData.admin_birth_date}
                  onChange={handleChange}
                  className={errors.admin_birth_date ? styles['is-invalid'] : ''}
                />
                {errors.admin_birth_date && (
                  <div className={styles['invalid-feedback']}>
                    <Icon icon="mdi:alert-circle" />
                    {errors.admin_birth_date}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-12">
              <div className={styles['form-group']}>
                <label>
                  Nome da Mãe <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="admin_mother_name"
                  value={formData.admin_mother_name}
                  onChange={handleChange}
                  className={errors.admin_mother_name ? styles['is-invalid'] : ''}
                  placeholder="Nome completo da mãe"
                />
                {errors.admin_mother_name && (
                  <div className={styles['invalid-feedback']}>
                    <Icon icon="mdi:alert-circle" />
                    {errors.admin_mother_name}
                  </div>
                )}
              </div>
            </div>

            {/* Campos adicionais do sócio */}
            <div className="col-md-6">
              <div className={styles['form-group']}>
                <label>
                  Renda Mensal <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="monthly_income"
                  value={formData.monthly_income}
                  onChange={handleChange}
                  className={errors.monthly_income ? styles['is-invalid'] : ''}
                  placeholder="R$ 5.000,00"
                />
                {errors.monthly_income && (
                  <div className={styles['invalid-feedback']}>
                    <Icon icon="mdi:alert-circle" />
                    {errors.monthly_income}
                  </div>
                )}
                <small className="text-muted">Renda mensal aproximada</small>
              </div>
            </div>

            <div className="col-md-6">
              <div className={styles['form-group']}>
                <label>
                  Ocupação Profissional <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="professional_occupation"
                  value={formData.professional_occupation}
                  onChange={handleChange}
                  className={errors.professional_occupation ? styles['is-invalid'] : ''}
                  placeholder="Ex: Empresário, Desenvolvedor, etc."
                />
                {errors.professional_occupation && (
                  <div className={styles['invalid-feedback']}>
                    <Icon icon="mdi:alert-circle" />
                    {errors.professional_occupation}
                  </div>
                )}
                <small className="text-muted">Profissão ou atividade principal</small>
              </div>
            </div>
          </div>

          {/* Seção: Endereço */}
          <div className={styles['section-header']} style={{ marginTop: '2rem' }}>
            <Icon icon="mdi:map-marker" />
            <span style={{ fontSize: '32px', display: 'block', fontWeight: '600' }}>Endereço</span>
          </div>

          <div className="row">
            <div className="col-md-4">
              <div className={styles['form-group']}>
                <label>
                  CEP <span className={styles.required}>*</span>
                  <Icon
                    icon="mdi:magnify"
                    style={{ marginLeft: '0.5rem', fontSize: '14px', color: '#6c757d' }}
                    title="Digite o CEP para preenchimento automático"
                  />
                </label>
                <div className={styles['cep-input-wrapper']}>
                  <input
                    type="text"
                    name="address_cep"
                    value={formData.address_cep}
                    onChange={handleChange}
                    className={errors.address_cep ? styles['is-invalid'] : ''}
                    placeholder="00000-000"
                    maxLength="9"
                    disabled={cepLoading}
                  />
                  {cepLoading && (
                    <div className={styles['cep-loading']}>
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Buscando...</span>
                      </div>
                    </div>
                  )}
                </div>
                {errors.address_cep && (
                  <div className={styles['invalid-feedback']}>
                    <Icon icon="mdi:alert-circle" />
                    {errors.address_cep}
                  </div>
                )}
                <span className={styles['form-text']}>Digite o CEP para preenchimento automático</span>
              </div>
            </div>

            <div className="col-md-6">
              <div className={styles['form-group']}>
                <label>
                  Rua <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="address_street"
                  value={formData.address_street}
                  onChange={handleChange}
                  className={errors.address_street ? styles['is-invalid'] : ''}
                  placeholder="Nome da rua"
                />
                {errors.address_street && (
                  <div className={styles['invalid-feedback']}>
                    <Icon icon="mdi:alert-circle" />
                    {errors.address_street}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-2">
              <div className={styles['form-group']}>
                <label>
                  Número <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="address_number"
                  value={formData.address_number}
                  onChange={handleChange}
                  className={errors.address_number ? styles['is-invalid'] : ''}
                  placeholder="Nº"
                />
                {errors.address_number && (
                  <div className={styles['invalid-feedback']}>
                    <Icon icon="mdi:alert-circle" />
                    {errors.address_number}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className={styles['form-group']}>
                <label>Complemento</label>
                <input
                  type="text"
                  name="address_complement"
                  value={formData.address_complement}
                  onChange={handleChange}
                  placeholder="Apt, bloco, etc (opcional)"
                />
              </div>
            </div>

            <div className="col-md-6">
              <div className={styles['form-group']}>
                <label>
                  Bairro <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="address_neighborhood"
                  value={formData.address_neighborhood}
                  onChange={handleChange}
                  className={errors.address_neighborhood ? styles['is-invalid'] : ''}
                  placeholder="Nome do bairro"
                />
                {errors.address_neighborhood && (
                  <div className={styles['invalid-feedback']}>
                    <Icon icon="mdi:alert-circle" />
                    {errors.address_neighborhood}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-8">
              <div className={styles['form-group']}>
                <label>
                  Cidade <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="address_city"
                  value={formData.address_city}
                  onChange={handleChange}
                  className={errors.address_city ? styles['is-invalid'] : ''}
                  placeholder="Nome da cidade"
                />
                {errors.address_city && (
                  <div className={styles['invalid-feedback']}>
                    <Icon icon="mdi:alert-circle" />
                    {errors.address_city}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-4">
              <div className={styles['form-group']}>
                <label>
                  Estado <span className={styles.required}>*</span>
                </label>
                <select
                  name="address_state"
                  value={formData.address_state}
                  onChange={handleChange}
                  className={errors.address_state ? styles['is-invalid'] : ''}
                >
                  <option value="">Selecione</option>
                  <option value="AC">Acre</option>
                  <option value="AL">Alagoas</option>
                  <option value="AP">Amapá</option>
                  <option value="AM">Amazonas</option>
                  <option value="BA">Bahia</option>
                  <option value="CE">Ceará</option>
                  <option value="DF">Distrito Federal</option>
                  <option value="ES">Espírito Santo</option>
                  <option value="GO">Goiás</option>
                  <option value="MA">Maranhão</option>
                  <option value="MT">Mato Grosso</option>
                  <option value="MS">Mato Grosso do Sul</option>
                  <option value="MG">Minas Gerais</option>
                  <option value="PA">Pará</option>
                  <option value="PB">Paraíba</option>
                  <option value="PR">Paraná</option>
                  <option value="PE">Pernambuco</option>
                  <option value="PI">Piauí</option>
                  <option value="RJ">Rio de Janeiro</option>
                  <option value="RN">Rio Grande do Norte</option>
                  <option value="RS">Rio Grande do Sul</option>
                  <option value="RO">Rondônia</option>
                  <option value="RR">Roraima</option>
                  <option value="SC">Santa Catarina</option>
                  <option value="SP">São Paulo</option>
                  <option value="SE">Sergipe</option>
                  <option value="TO">Tocantins</option>
                </select>
                {errors.address_state && (
                  <div className={styles['invalid-feedback']}>
                    <Icon icon="mdi:alert-circle" />
                    {errors.address_state}
                  </div>
                )}
              </div>
            </div>

            {/* Ponto de Referência */}
            <div className="col-md-12">
              <div className={styles['form-group']}>
                <label>
                  Ponto de Referência <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="address_reference"
                  value={formData.address_reference}
                  onChange={handleChange}
                  className={errors.address_reference ? styles['is-invalid'] : ''}
                  placeholder="Ex: Próximo ao mercado central, em frente à praça..."
                />
                {errors.address_reference && (
                  <div className={styles['invalid-feedback']}>
                    <Icon icon="mdi:alert-circle" />
                    {errors.address_reference}
                  </div>
                )}
                <small className="text-muted">Referência para localização</small>
              </div>
            </div>
          </div>

          {/* Seção: Documentos */}
          <div className={styles['section-header']} style={{ marginTop: '2rem' }}>
            <Icon icon="mdi:file-document" />
            <span style={{ fontSize: '32px', display: 'block', fontWeight: '600' }}>
              {personType === 'PJ' ? 'Documentos do Sócio Administrador' : 'Documento de Identificação'}
            </span>
          </div>

          <div className={styles['documents-section']}>
            <div className={styles['document-disclaimer']}>
              <Icon icon="mdi:information" />
              <p>
                <strong>Documento obrigatório:</strong> Envie um documento com foto (CNH ou RG).
              </p>
            </div>

            <div className={styles['upload-group']}>
              <label>
                Documento Pessoal (CNH ou RG) <span className={styles.required}>*</span>
              </label>

              {/* Mostrar documento existente */}
              {!documents.cnh && existingDocuments.cnh && (
                <div className={styles['existing-document']}>
                  <Icon icon="mdi:check-circle" style={{ color: '#28a745' }} />
                  <span>
                    Documento já enviado: <strong>{existingDocuments.cnh.file_name || 'CNH/RG'}</strong>
                    <small>Enviado em {new Date(existingDocuments.cnh.uploaded_at || existingDocuments.cnh.created_at).toLocaleDateString('pt-BR')}</small>
                  </span>
                </div>
              )}

              <div className={styles['file-input-wrapper']}>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleDocumentUpload('cnh', e.target.files[0])}
                  className={styles['file-input']}
                  id="doc-cnh"
                />
                <label htmlFor="doc-cnh" className={styles['file-label']}>
                  <Icon icon={documents.cnh ? "mdi:file-check" : existingDocuments.cnh ? "mdi:file-replace" : "mdi:cloud-upload"} />
                  {documents.cnh ? (
                    <span className={styles['file-selected']}>
                      {documents.cnh.name}
                      <small className={styles['file-status']}>Aguardando envio (substituirá o existente)</small>
                    </span>
                  ) : existingDocuments.cnh ? (
                    'Substituir documento'
                  ) : (
                    'Selecionar arquivo'
                  )}
                </label>
                {documents.cnh && (
                  <button
                    type="button"
                    className={styles['btn-remove-file']}
                    onClick={() => handleRemoveDocument('cnh')}
                    title="Remover arquivo"
                  >
                    <Icon icon="mdi:close" />
                  </button>
                )}
              </div>
              {errors.cnh && (
                <div className={styles['invalid-feedback']}>
                  <Icon icon="mdi:alert-circle" />
                  {errors.cnh}
                </div>
              )}
              <small className={styles['file-hint']}>
                Formatos aceitos: JPG, PNG, PDF | Tamanho máximo: 5MB
              </small>
            </div>
          </div>

          {/* Campos de Empresa (apenas PJ) */}
          {personType === 'PJ' && (
            <>
              <div className={styles['section-header']} style={{ marginTop: '2rem' }}>
                <Icon icon="mdi:office-building" />
                <span style={{ fontSize: '32px', display: 'block', fontWeight: '600' }}>Dados da Empresa</span>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <div className={styles['form-group']}>
                    <label>
                      CNPJ <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      name="responsible_cpf"
                      value={formData.responsible_cpf}
                      onChange={handleChange}
                      className={errors.responsible_cpf ? styles['is-invalid'] : ''}
                      placeholder="00.000.000/0000-00"
                      maxLength="18"
                    />
                    {errors.responsible_cpf && (
                      <div className={styles['invalid-feedback']}>
                        <Icon icon="mdi:alert-circle" />
                        {errors.responsible_cpf}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-4">
                  <div className={styles['form-group']}>
                    <label>Inscrição Estadual</label>
                    <input
                      type="text"
                      name="state_registration"
                      value={formData.state_registration}
                      onChange={handleChange}
                      placeholder="000.000.000.000 (opcional)"
                    />
                  </div>
                </div>

                <div className="col-md-4">
                  <div className={styles['form-group']}>
                    <label>
                      Telefone da Empresa <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      name="responsible_phone"
                      value={formData.responsible_phone}
                      onChange={handleChange}
                      className={errors.responsible_phone ? styles['is-invalid'] : ''}
                      placeholder="(00) 00000-0000"
                      maxLength="15"
                    />
                    {errors.responsible_phone && (
                      <div className={styles['invalid-feedback']}>
                        <Icon icon="mdi:alert-circle" />
                        {errors.responsible_phone}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className={styles['form-group']}>
                    <label>
                      Razão Social <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      className={errors.company_name ? styles['is-invalid'] : ''}
                      placeholder="Nome legal da empresa"
                    />
                    {errors.company_name && (
                      <div className={styles['invalid-feedback']}>
                        <Icon icon="mdi:alert-circle" />
                        {errors.company_name}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className={styles['form-group']}>
                    <label>
                      Nome Fantasia <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      name="trading_name"
                      value={formData.trading_name}
                      onChange={handleChange}
                      className={errors.trading_name ? styles['is-invalid'] : ''}
                      placeholder="Nome comercial da empresa"
                    />
                    {errors.trading_name && (
                      <div className={styles['invalid-feedback']}>
                        <Icon icon="mdi:alert-circle" />
                        {errors.trading_name}
                      </div>
                    )}
                  </div>
                </div>

                {/* Novos campos da empresa */}
                <div className="col-md-6">
                  <div className={styles['form-group']}>
                    <label>
                      Data de Fundação <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="date"
                      name="founding_date"
                      value={formData.founding_date}
                      onChange={handleChange}
                      className={errors.founding_date ? styles['is-invalid'] : ''}
                    />
                    {errors.founding_date && (
                      <div className={styles['invalid-feedback']}>
                        <Icon icon="mdi:alert-circle" />
                        {errors.founding_date}
                      </div>
                    )}
                    <small className="text-muted">Data de abertura da empresa</small>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className={styles['form-group']}>
                    <label>
                      Site da Empresa <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="url"
                      name="site_url"
                      value={formData.site_url}
                      onChange={handleChange}
                      className={errors.site_url ? styles['is-invalid'] : ''}
                      placeholder="https://www.empresa.com.br"
                    />
                    {errors.site_url && (
                      <div className={styles['invalid-feedback']}>
                        <Icon icon="mdi:alert-circle" />
                        {errors.site_url}
                      </div>
                    )}
                    <small className="text-muted">URL completa do site da empresa</small>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className={styles['form-group']}>
                    <label>
                      Faturamento Anual <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      name="annual_revenue"
                      value={formData.annual_revenue}
                      onChange={handleChange}
                      className={errors.annual_revenue ? styles['is-invalid'] : ''}
                      placeholder="R$ 120.000,00"
                    />
                    {errors.annual_revenue && (
                      <div className={styles['invalid-feedback']}>
                        <Icon icon="mdi:alert-circle" />
                        {errors.annual_revenue}
                      </div>
                    )}
                    <small className="text-muted">Faturamento anual da empresa</small>
                  </div>
                </div>
              </div>

              {/* Seção: Documentos da Empresa */}
              <div className={styles['section-header']} style={{ marginTop: '2rem' }}>
                <Icon icon="mdi:file-document-multiple" />
                <span style={{ fontSize: '32px', display: 'block', fontWeight: '600' }}>Documentos da Empresa</span>
              </div>

              <div className={styles['documents-section']}>
                <div className={styles['document-disclaimer']}>
                  <Icon icon="mdi:information" />
                  <p>
                    <strong>Documento obrigatório:</strong> Envie o Cartão CNPJ <strong>OU</strong> Inscrição Estadual.
                  </p>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className={styles['upload-group']}>
                      <label>Cartão CNPJ</label>
                      <div className={styles['file-input-wrapper']}>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleDocumentUpload('cnpj', e.target.files[0])}
                          className={styles['file-input']}
                          id="doc-cnpj"
                        />
                        <label htmlFor="doc-cnpj" className={styles['file-label']}>
                          <Icon icon={documents.cnpj ? "mdi:file-check" : "mdi:cloud-upload"} />
                          {documents.cnpj ? (
                            <span className={styles['file-selected']}>
                              {documents.cnpj.name}
                              <small className={styles['file-status']}>Aguardando envio</small>
                            </span>
                          ) : (
                            'Selecionar arquivo'
                          )}
                        </label>
                        {documents.cnpj && (
                          <button
                            type="button"
                            className={styles['btn-remove-file']}
                            onClick={() => handleRemoveDocument('cnpj')}
                            title="Remover arquivo"
                          >
                            <Icon icon="mdi:close" />
                          </button>
                        )}
                      </div>
                      <small className={styles['file-hint']}>
                        Formatos aceitos: JPG, PNG, PDF | Máx: 5MB
                      </small>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className={styles['upload-group']}>
                      <label>Inscrição Estadual</label>
                      <div className={styles['file-input-wrapper']}>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleDocumentUpload('inscricao_estadual', e.target.files[0])}
                          className={styles['file-input']}
                          id="doc-inscricao"
                        />
                        <label htmlFor="doc-inscricao" className={styles['file-label']}>
                          <Icon icon={documents.inscricao_estadual ? "mdi:file-check" : "mdi:cloud-upload"} />
                          {documents.inscricao_estadual ? (
                            <span className={styles['file-selected']}>
                              {documents.inscricao_estadual.name}
                              <small className={styles['file-status']}>Aguardando envio</small>
                            </span>
                          ) : (
                            'Selecionar arquivo'
                          )}
                        </label>
                        {documents.inscricao_estadual && (
                          <button
                            type="button"
                            className={styles['btn-remove-file']}
                            onClick={() => handleRemoveDocument('inscricao_estadual')}
                            title="Remover arquivo"
                          >
                            <Icon icon="mdi:close" />
                          </button>
                        )}
                      </div>
                      <small className={styles['file-hint']}>
                        Formatos aceitos: JPG, PNG, PDF | Máx: 5MB
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Aviso sobre envio de documentos */}
          {(documents.cnh || documents.cnpj || documents.inscricao_estadual) && (
            <div className={styles['upload-reminder']}>
              <Icon icon="mdi:information" />
              <p>
                <strong>Atenção:</strong> Os documentos selecionados serão enviados ao clicar em "Finalizar Cadastro".
              </p>
            </div>
          )}

          {/* Footer com botões de ação */}
          <div className={styles['form-actions']}>
            <button
              type="button"
              className={styles['btn-cancel']}
              onClick={onCancel}
              disabled={submitting}
            >
              <Icon icon="mdi:close" />
              Cancelar
            </button>

            <button
              type="submit"
              className={styles['btn-submit']}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Enviando...</span>
                  </div>
                  Finalizando...
                </>
              ) : (
                <>
                  <Icon icon="mdi:check-circle" />
                  Finalizar Cadastro
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ResponsibleFinalizationForm;
